import React, { useState } from 'react';
import {
  TextField,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormLabel,
  RadioGroup,
  Radio,
  Select,
  MenuItem,
  InputLabel,
  Rating,
  Button,
  Box,
  Typography,
  Chip,
  FormHelperText,
  InputAdornment,
} from '@mui/material';
import {
  PhotoCamera,
  Upload,
  Draw,
} from '@mui/icons-material';
import { DatePicker, TimePicker, DateTimePicker } from '@mui/x-date-pickers';
import { TaskDataField } from '../../types/index';

interface DynamicDataFieldInputProps {
  field: TaskDataField;
  value: any;
  onChange: (value: any) => void;
  error?: string;
}

const DynamicDataFieldInput: React.FC<DynamicDataFieldInputProps> = ({
  field,
  value,
  onChange,
  error,
}) => {
  const [localValue, setLocalValue] = useState(value || '');

  const handleChange = (newValue: any) => {
    setLocalValue(newValue);
    onChange(newValue);
  };

  const renderInput = () => {
    switch (field.input_type) {
      case 'text':
        return (
          <TextField
            fullWidth
            label={field.field_label}
            value={localValue}
            onChange={(e) => handleChange(e.target.value)}
            placeholder={field.placeholder_text}
            helperText={field.help_text}
            required={field.is_required}
            error={!!error}
            multiline={field.field_name.includes('notes') || field.field_name.includes('description')}
            rows={field.field_name.includes('notes') || field.field_name.includes('description') ? 3 : 1}
          />
        );

      case 'number':
        return (
          <TextField
            fullWidth
            type="number"
            label={field.field_label}
            value={localValue}
            onChange={(e) => handleChange(parseFloat(e.target.value) || 0)}
            placeholder={field.placeholder_text}
            helperText={field.help_text}
            required={field.is_required}
            error={!!error}
            inputProps={{
              min: field.min_value,
              max: field.max_value,
              step: 1 / Math.pow(10, field.decimal_places || 0),
            }}
          />
        );

      case 'measurement':
        return (
          <TextField
            fullWidth
            type="number"
            label={field.field_label}
            value={localValue}
            onChange={(e) => handleChange(parseFloat(e.target.value) || 0)}
            placeholder={field.placeholder_text}
            helperText={field.help_text}
            required={field.is_required}
            error={!!error}
            InputProps={{
              endAdornment: field.unit_of_measure ? (
                <InputAdornment position="end">
                  <Chip label={field.unit_of_measure} size="small" />
                </InputAdornment>
              ) : undefined,
            }}
            inputProps={{
              min: field.min_value,
              max: field.max_value,
              step: 1 / Math.pow(10, field.decimal_places || 2),
            }}
          />
        );

      case 'checkbox':
        return (
          <FormControlLabel
            control={
              <Checkbox
                checked={!!localValue}
                onChange={(e) => handleChange(e.target.checked)}
              />
            }
            label={field.field_label}
          />
        );

      case 'yes_no':
        return (
          <FormControl component="fieldset" required={field.is_required} error={!!error}>
            <FormLabel component="legend">{field.field_label}</FormLabel>
            <RadioGroup
              row
              value={localValue}
              onChange={(e) => handleChange(e.target.value)}
            >
              <FormControlLabel value="yes" control={<Radio />} label="Yes" />
              <FormControlLabel value="no" control={<Radio />} label="No" />
            </RadioGroup>
            {field.help_text && <FormHelperText>{field.help_text}</FormHelperText>}
          </FormControl>
        );

      case 'pass_fail':
        return (
          <FormControl component="fieldset" required={field.is_required} error={!!error}>
            <FormLabel component="legend">{field.field_label}</FormLabel>
            <RadioGroup
              row
              value={localValue}
              onChange={(e) => handleChange(e.target.value)}
            >
              <FormControlLabel value="pass" control={<Radio />} label="Pass" />
              <FormControlLabel value="fail" control={<Radio />} label="Fail" />
            </RadioGroup>
            {field.help_text && <FormHelperText>{field.help_text}</FormHelperText>}
          </FormControl>
        );

      case 'dropdown':
        return (
          <FormControl fullWidth required={field.is_required} error={!!error}>
            <InputLabel>{field.field_label}</InputLabel>
            <Select
              value={localValue}
              onChange={(e) => handleChange(e.target.value)}
              label={field.field_label}
            >
              {field.options?.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
            {field.help_text && <FormHelperText>{field.help_text}</FormHelperText>}
          </FormControl>
        );

      case 'multi_select':
        return (
          <FormControl fullWidth required={field.is_required} error={!!error}>
            <InputLabel>{field.field_label}</InputLabel>
            <Select
              multiple
              value={Array.isArray(localValue) ? localValue : []}
              onChange={(e) => handleChange(e.target.value)}
              label={field.field_label}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {(selected as string[]).map((value) => (
                    <Chip key={value} label={value} size="small" />
                  ))}
                </Box>
              )}
            >
              {field.options?.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
            {field.help_text && <FormHelperText>{field.help_text}</FormHelperText>}
          </FormControl>
        );

      case 'rating':
        return (
          <Box>
            <Typography component="legend">{field.field_label}</Typography>
            <Rating
              value={localValue ? parseFloat(localValue) : 0}
              onChange={(_, newValue) => handleChange(newValue)}
              max={field.max_value || 5}
              precision={1 / Math.pow(10, field.decimal_places || 0)}
            />
            {field.help_text && (
              <Typography variant="caption" color="text.secondary">
                {field.help_text}
              </Typography>
            )}
          </Box>
        );

      case 'date':
        return (
          <DatePicker
            label={field.field_label}
            value={localValue ? new Date(localValue) : null}
            onChange={(newValue) => handleChange(newValue?.toISOString().split('T')[0])}
            slotProps={{
              textField: {
                fullWidth: true,
                required: field.is_required,
                error: !!error,
                helperText: error || field.help_text,
              },
            }}
          />
        );

      case 'time':
        return (
          <TimePicker
            label={field.field_label}
            value={localValue ? new Date(`2000-01-01T${localValue}`) : null}
            onChange={(newValue) => handleChange(newValue?.toTimeString().split(' ')[0])}
            slotProps={{
              textField: {
                fullWidth: true,
                required: field.is_required,
                error: !!error,
                helperText: error || field.help_text,
              },
            }}
          />
        );

      case 'datetime':
        return (
          <DateTimePicker
            label={field.field_label}
            value={localValue ? new Date(localValue) : null}
            onChange={(newValue) => handleChange(newValue?.toISOString())}
            slotProps={{
              textField: {
                fullWidth: true,
                required: field.is_required,
                error: !!error,
                helperText: error || field.help_text,
              },
            }}
          />
        );

      case 'photo':
        return (
          <Box>
            <Button
              variant="outlined"
              component="label"
              startIcon={<PhotoCamera />}
              fullWidth
            >
              {field.field_label}
              <input
                type="file"
                accept="image/*"
                hidden
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    // In a real implementation, you'd upload the file and store the path
                    handleChange(`photo_${Date.now()}_${file.name}`);
                  }
                }}
              />
            </Button>
            {localValue && (
              <Typography variant="caption" color="text.secondary">
                Selected: {localValue}
              </Typography>
            )}
            {field.help_text && (
              <Typography variant="caption" color="text.secondary">
                {field.help_text}
              </Typography>
            )}
          </Box>
        );

      case 'file_upload':
        return (
          <Box>
            <Button
              variant="outlined"
              component="label"
              startIcon={<Upload />}
              fullWidth
            >
              {field.field_label}
              <input
                type="file"
                hidden
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    handleChange(`file_${Date.now()}_${file.name}`);
                  }
                }}
              />
            </Button>
            {localValue && (
              <Typography variant="caption" color="text.secondary">
                Selected: {localValue}
              </Typography>
            )}
            {field.help_text && (
              <Typography variant="caption" color="text.secondary">
                {field.help_text}
              </Typography>
            )}
          </Box>
        );

      case 'signature':
        return (
          <Box>
            <Button
              variant="outlined"
              startIcon={<Draw />}
              fullWidth
              onClick={() => {
                // In a real implementation, this would open a signature pad
                handleChange(`signature_${Date.now()}`);
              }}
            >
              {field.field_label}
            </Button>
            {localValue && (
              <Typography variant="caption" color="text.secondary">
                Signature captured: {localValue}
              </Typography>
            )}
            {field.help_text && (
              <Typography variant="caption" color="text.secondary">
                {field.help_text}
              </Typography>
            )}
          </Box>
        );

      default:
        return (
          <TextField
            fullWidth
            label={field.field_label}
            value={localValue}
            onChange={(e) => handleChange(e.target.value)}
            placeholder={field.placeholder_text}
            helperText={field.help_text}
            required={field.is_required}
            error={!!error}
          />
        );
    }
  };

  return (
    <Box sx={{ mb: 2 }}>
      {renderInput()}
      {error && (
        <Typography variant="caption" color="error">
          {error}
        </Typography>
      )}
    </Box>
  );
};

export default DynamicDataFieldInput; 